//PlayCanvasHelper
import * as pc from 'playcanvas';
import {GetPerformanceInfo} from './PerformanceInfo';

export const DestopScaler = 1.5;
export const MobileScaler = 1.2;

export let paused: boolean = false;

export let canvas: HTMLCanvasElement;
export function SetCanvas(newCanvas: HTMLCanvasElement) : void {
  canvas = newCanvas;
}

export let app : pc.Application;
export function SetApp(newApp: pc.Application) : void {
  app = newApp;
}

export let isMobile : boolean = false;
export function SetIsMobile(newIsMobile: boolean) : void {
  isMobile = newIsMobile;
}

export function PauseApp() : void {
  if(!app)
    return;
  
  app.timeScale = 0; // detiene las físicas
  app.autoRender = false; // detiene los frames
  FullCanvasDetach();
  paused = true;
}

export function ResumeApp() : void {
  if(!app || !paused)
    return;
  
  app.timeScale = 1; // reaunuda las físicas
  app.autoRender = true; // reaunuda los frames
  //app.renderNextFrame = true; //si se quiere controlando el frame
  FullCanvasAttach();
  paused = false;
}

export function DestroyApp() : void {
  if(!app)
    return;
  
  try {
    app.destroy();
    const gl = app.graphicsDevice?.gl;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
    console.log("recursos de a-frame desmontados correctamente");
  } finally {
    
  }
}

export let camera: pc.CameraComponent;
export function SetMainCamera(mainCamera: pc.CameraComponent) : void {
  camera = mainCamera;
}

export async function LoadTexture(name: string, url: string) : Promise<pc.Texture> {
  return new Promise((resolve) => {
    const asset = new pc.Asset(name, "texture", { url: url });
    app.assets.add(asset);
    asset.ready(() => {
      resolve(asset.resources[0] as pc.Texture);
    });
    app.assets.load(asset);
  });
}

export async function LoadSingleAssetSimple(asset: pc.Asset) : Promise<void> {
  return new Promise((resolve) => {
    asset.ready(() => { resolve() });
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

export async function LoadSingleAssetEstimated(asset: pc.Asset, setProgress: (value: number) => void, loadingStartValue: number = -1, loadingEndValue: number = -1, intervalSeconds: number = 1, incrementPerc: number = 1) : Promise<void> {
  return new Promise((resolve) => {
    let intervalId: NodeJS.Timeout;
      let intervalCleared = true;
      if(loadingStartValue >= 0 && loadingEndValue >= 0){
        let perc = loadingStartValue;
        intervalCleared = false;
        intervalId = setInterval(() => {
          perc = perc + incrementPerc;
          if(perc > loadingEndValue){
            perc = loadingEndValue;
            intervalCleared = true;
            clearInterval(intervalId);
          }
          setProgress(perc);
        }, 1000 * intervalSeconds);
        
      }

      app.assets.add(asset);
      asset.ready(() => {
        if(intervalCleared === false)
          clearInterval(intervalId);
        if(loadingStartValue >= 0 && loadingEndValue >= 0){
          setProgress(loadingEndValue);
        }
        resolve();
      });
      app.assets.load(asset);
  });
}

export async function LoadSingleAsset(asset: pc.Asset, setProgress: (value: number) => void, loadingStartValue: number = -1, loadingEndValue: number = -1) : Promise<void> {
  return new Promise((resolve) => {
    asset.on('progress', (recv, total) => {
      if (!isFinite(total)) return; // sin Content-Length → salimos
      if(loadingStartValue >= 0 && loadingEndValue >= 0){
        const pct = (recv / total);
        setProgress(loadingStartValue + (loadingEndValue - loadingStartValue) * pct);
      }
    });
    asset.ready(() => {
      if(loadingStartValue >= 0 && loadingEndValue >= 0){
        setProgress(loadingEndValue);
      }
      resolve();
    });
    app.assets.add(asset);
    app.assets.load(asset);
  });
}

export async function LoadMultipleAssets(assets: Object, setProgress: (value: number) => void, loadingStartValue: number = -1, loadingEndValue: number = -1) : Promise<void> {
  return new Promise((resolve) => {
    let currentLoaded = 0;
    const assetListLoader = new pc.AssetListLoader(Object.values(assets), app.assets);
    assetListLoader.on("progress", (asset: pc.Asset) => {
      if(loadingStartValue >= 0 && loadingEndValue >= 0){
        currentLoaded = currentLoaded + 1;
        const percent = Math.floor((currentLoaded / Object.values(assets).length));
        setProgress(loadingStartValue + (loadingEndValue - loadingStartValue) * percent);
      }
    });
    assetListLoader.load(() => {
      if(loadingStartValue >= 0 && loadingEndValue >= 0){
        setProgress(loadingEndValue);
      }
      resolve();
    });
  })
}

export async function LoadDracoFiles() : Promise<void> {
  return new Promise((resolve) => {
    pc.WasmModule.setConfig('DracoDecoderModule', {
      glueUrl: `./draco/draco.wasm.js`,
      wasmUrl: `./draco/draco.wasm.wasm`,
      fallbackUrl: `./draco/draco.js`
    });
    pc.WasmModule.getInstance('DracoDecoderModule', () => {
      resolve();
    });
  })
};

export async function WaitForSeconds(seconds: number) : Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, 1000 * seconds);
  });
}

//fov = 2⋅arctan(sensorHeight / 2⋅focalLenght), en Radianes
export function FocalLenght2FOV(focalLenght: number, sensorHeight: number = 24) : number {
  const radians = 2 * Math.atan(sensorHeight / (2 * focalLenght));
  return Radians2Degrees(radians);
};

//focalLenght = sensorHeight / (2⋅Math.tan(fov/2))
export function FOV2FocalLenght(fov: number, sensorHeight: number = 24) : number {
  return sensorHeight / (2 * Math.tan(0.5 * Degrees2Radians(fov)));
};

export function MAXFov2PCFov(fov: number, aspect: number) : number {
  const hfov = Degrees2Radians(fov);
  const vfov = 2 * Math.atan(Math.tan(hfov / 2) / aspect) * 180 / Math.PI;
  return vfov;
};

export function Degrees2Radians(degrees: number) : number {
  return degrees * Math.PI / 180;
};

export function Radians2Degrees(radians: number) : number {
  return radians * 180 / Math.PI
};

export function GetIDFromNodeName(input: string) : string {
  const lastElement = input.split("@").at(-1); // Get the last element
  return lastElement ? lastElement : "";
};

export function GetNameFromNodeName(input: string) : string {
  const firstElement = input.split("@").at(0); // Get the first element
  return firstElement ? firstElement : "";
};

export function GetAptoColorByAvailability(availability: string) : string {
  if(availability === "DISPONIBLE")
    return "#00FA00"
  if(availability === "NO DISPONIBLE")
    return "#000000"
  if(availability === "RESERVADO")
    return "#EEA63A"
  if(availability === "VENDIDO")
    return "#FF0000"
  return '#FFFFFF'
};

export function GetAptoColorByType(property: any, attributesType: any) : string {
  for(const value of property.values){
    if(value.attribute === attributesType[0].attribute){
      for(const attributeType of attributesType){
        if(value.IdValue === attributeType.id){
          return attributeType.layoutFill
        }
      }
    }
  }
  return '#FFFFFF';
};

export function GetRGBFromHexString(hexColor: string) {
  const color = new pc.Color();
  color.fromString(hexColor);
  const r = Math.round(255*color.r);
  const g = Math.round(255*color.g);
  const b = Math.round(255*color.b);
  return {r: r, g: g, b: b};
};

export function FilterProperty(property: any, allFilters: any, availability: any) : boolean {
  let filtered = true;
  for(const filterKey in allFilters){
    const filters = allFilters[filterKey]
    if(filters.length > 0){
      let attributeFilter = false;
      for(const filter of filters){
        for(const value of property.values){
          if(value.attribute === filter.attribute){
            attributeFilter = attributeFilter || (value.IdValue === filter.id)
          }
          if(attributeFilter === true)
            break;
        }
      }
      filtered = filtered && attributeFilter;
    }
  }
  filtered = filtered && availability[property.availability];
  return filtered;
};

export function EnablePickRecursive(entity: pc.Entity|pc.GraphNode, enable: boolean = true) : void {
  if (entity instanceof pc.Entity) {
    if(entity.gsplat && entity.gsplat.instance && entity.gsplat.instance.meshInstance){
      entity.gsplat.instance.meshInstance.pick = enable;
    }else if(entity.render && entity.render.meshInstances){
      for(const mesh of entity.render.meshInstances){
        mesh.pick = enable;
      }
    }
  }
  for(const child of entity.children){
    EnablePickRecursive(child, enable);
  }
};

export function AssignLayerRecursive(entity: pc.Entity|pc.GraphNode, layerId: number) : void {
  if (entity instanceof pc.Entity && entity.render) {
    entity.render.layers = [layerId];
  }
  for(const child of entity.children){
    AssignLayerRecursive(child, layerId);
  }
};

export function CreateLayoutMaterialFromColor(name: string, color: pc.Color, transparent: boolean = false, alpha: number = 1, depthWrite: boolean = true) : pc.StandardMaterial {
  const material = new pc.StandardMaterial();
  material.name = name;
  material.emissive = color.clone();
  material.specular = color.clone();
  material.useLighting = false;
  material.cull = pc.CULLFACE_NONE;
  material.blendType = transparent === true ? pc.BLEND_NORMAL : pc.BLEND_NONE;
  material.opacity = alpha;
  material.depthWrite = depthWrite; //necesario para picking con transparencia
  material.update();
  return material;
};

export function SoftTextures(texture: pc.Texture, anisotropy: number = 4, mipmaps: boolean = true, minFilter: number = pc.FILTER_LINEAR_MIPMAP_NEAREST, magFilter: number = pc.FILTER_LINEAR) {
  texture.addressU = pc.ADDRESS_CLAMP_TO_EDGE;
  texture.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
  texture.minFilter = minFilter;
  texture.magFilter = magFilter;
  texture.anisotropy = anisotropy;
  texture.mipmaps = mipmaps;
  return texture;
};

export function Rgb2Hsl(color: pc.Color): { h: number; s: number; l: number } {
  const r = color.r;
  const g = color.g;
  const b = color.b;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0));
        break;
      case g:
        h = ((b - r) / d + 2);
        break;
      case b:
        h = ((r - g) / d + 4);
        break;
    }

    h /= 6;
  }

  return { h, s, l };
}

export function Hsl2Rgb(h: number, s: number = 1, l: number = 1): pc.Color {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // Achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return new pc.Color(r, g, b);
}

export function NormalizeAngle(angle: number, nextAngle: number | null = null) : number {
  if(!nextAngle)
    return angle % 360;
  const angle0 = angle % 360;
  const angleP = angle0 + 360;
  const angleN = angle0 - 360;
  const diff0 = Math.abs(angle0 - nextAngle);
  const diffP = Math.abs(angleP - nextAngle);
  const diffN = Math.abs(angleN - nextAngle);
  if(diffP < diff0 && diffP < diffN)
    return angleP;
  if(diffN < diff0 && diffN < diffP)
    return angleN;
  return angle0;
};

export function GetScreenPosition(worldPosition: pc.Vec3, canvasOffset: pc.Vec2 = new pc.Vec2(0, 0)) : pc.Vec2 {
  const screenPoint = new pc.Vec3();
  camera.worldToScreen(worldPosition, screenPoint);

  const screenPosition = new pc.Vec2();
  screenPosition.x = screenPoint.x - canvasOffset.x;
  screenPosition.y = screenPoint.y - canvasOffset.y;
  return screenPosition;
};

export function GetPickedMesh(event: PointerEvent, camera: pc.CameraComponent) : pc.GraphNode|null {
  const x = event.clientX;
  const y = event.clientY;
  
  const pickerScale = 0.25; // Lets use quarter of the resolution to improve performance - this will miss very small objects, but it's ok in our case
  const picker = new pc.Picker(app, canvas.clientWidth * pickerScale, canvas.clientHeight * pickerScale);

  const worldLayer = app.scene.layers.getLayerByName('World')!;
  picker.prepare(camera, app.scene, [worldLayer]);

  const meshInstances = picker.getSelection(x * pickerScale, y * pickerScale, 1, 1);
  return meshInstances.length > 0 ? meshInstances[0].node : null; 
};

export function FullCanvasAttach(divElement: any = null) : void {
  const element = divElement ? divElement : canvas; 
  app.mouse?.attach(element as any);
  app.elementInput?.attach(element as any);
  app.touch?.attach(element as any);
}

export function FullCanvasDetach() : void {
  app.mouse?.detach();
  app.elementInput?.detach();
  app.touch?.detach();
}

export function CreateSplatInstance(name: string, asset: object, gsplatLayer: pc.Layer|null = null, introAnimation: boolean = false, vertex: any = null, fragment: any = null) {
  const entityParent = new pc.Entity(`${name}_parent`);
  const entity = new pc.Entity(name);
  const gsplat = entity.addComponent('gsplat', { asset: asset }) as pc.GSplatComponent;
  const gsplatMaterial = gsplat!.material!;
  if(gsplatLayer){ //layer config
    gsplat!.layers = [gsplatLayer.id];
    gsplatMaterial.depthWrite = false;
    gsplatMaterial.depthTest = true;
    gsplatMaterial.update();
  }
  entity.setLocalPosition(0, 0, 0);
  entity.setLocalEulerAngles(180, 0, 0);
  entity.setLocalScale(1, 1, 1);
  
  if(introAnimation){
    gsplatMaterial.shaderDesc!.fragmentGLSL = fragment;
    gsplatMaterial.shaderDesc!.vertexGLSL = vertex;
    const a = 'gl_FragColor = vec4(gammaCorrectOutput(toneMap(processEnvironment(linear))), 1.0);';
    const b = 'gl_FragColor = vec4(gammaCorrectOutput(toneMap(processEnvironment(linear))), 0.0);';
    pc.shaderChunks['skyboxPS'] = pc.shaderChunks['skyboxPS'].replace(a, b);
  }

  entityParent.addChild(entity);
  app.root.addChild(entityParent);

  return {gsplatNode: entityParent, gsplat: gsplat, gsplatMaterial: gsplatMaterial};
};

export function CreateRoundBillboardTextNode(billboardAssets: any, fontAsset: number, texto: string, color: pc.Color, colorFill: pc.Color) : pc.Entity {
  const worldScreen = new pc.Entity("WorldScreen"+texto);
  worldScreen.addComponent("screen", {
    screenSpace: false,
    resolution: pc.Vec2.ONE,
    referenceResolution: new pc.Vec2(1280, 720),
    scaleMode: "blend",
  });
  worldScreen.setLocalEulerAngles(0, 180, 0);
  worldScreen.setLocalScale(0.01, 0.01, 0.01);

  const background = new pc.Entity("BG"+texto);
  background.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.propertyTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: colorFill,
    width: 1000,
    height: 1000,
  });
  background.element!.layers = [pc.LAYERID_WORLD];
  worldScreen.addChild(background);

  const text = new pc.Entity("Text3D"+texto);
  text.addComponent("element", {
    type: pc.ELEMENTTYPE_TEXT,
    fontAsset: fontAsset,
    text: ` ${texto} `,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0, 0, 1, 1),
    margin: new pc.Vec4(0, 0, 0, 0),
    autoWidth: false,
    autoHeight: false,
    autoFitWidth: true,
    autoFitHeight: true,
    minFontSize: 100,
    maxFontSize: 1000,
    color: color,
    useInput: true
  });
  text.element!.layers = [pc.LAYERID_WORLD];
  background.addChild(text);

  return worldScreen;
}

export async function CreateAmenityBillboard(billboardAssets: any, cameraEntity: any, isPortraitRef: any, billboardParent: pc.Entity, amenityID: string, endPointNode: pc.GraphNode, iconURL: string) : Promise<pc.Entity> {
  const baseSize = 2;
  /////padre
  const worldScreen = new pc.Entity("WorldScreen_"+amenityID);
  worldScreen.addComponent("screen", {
    screenSpace: false,
    resolution: pc.Vec2.ONE,
    referenceResolution: new pc.Vec2(1280, 720),
  });
  billboardParent.addChild(worldScreen);
  
  /////dona de abajo (start point) OKKKKKK
  const startPointEntity = new pc.Entity("startPoint_"+amenityID);
  startPointEntity.addComponent("element",{width: 0, height: 0});
  worldScreen.addChild(startPointEntity);
  const startPointIcon = new pc.Entity("startPointIcon_"+amenityID);
  startPointIcon.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.amenityBaseTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: new pc.Color(1,1,1),
    width: baseSize,
    height: baseSize,
    useInput: true,
  });
  startPointIcon.element!.layers = [pc.LAYERID_WORLD];
  startPointIcon.setLocalEulerAngles(0, 180, 0);
  startPointEntity.addChild(startPointIcon);

  //End point
  const endPointEntity = new pc.Entity("Billboard_"+amenityID);
  worldScreen.addChild(endPointEntity);
  endPointEntity.setPosition(endPointNode.getPosition().clone());
  endPointEntity.addComponent("element",{width: 0, height: 0});
  endPointEntity.element!.layers = [pc.LAYERID_WORLD];
  
  //billboard background (on endpoint)
  const billboardBackground = new pc.Entity("BillboardBG_"+amenityID);
  billboardBackground.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.propertyTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: new pc.Color(1,1,1),
    width: 5*baseSize,
    height: 5*baseSize,
    useInput: true,
  });
  billboardBackground.element!.layers = [pc.LAYERID_WORLD];
  billboardBackground.setLocalEulerAngles(0, 180, 0);
  billboardBackground.setLocalPosition(0, 5*baseSize/2, 0);
  endPointEntity.addChild(billboardBackground);

  const textureIcon = await LoadTexture("icon", iconURL);
  SoftTextures(textureIcon);
  let planeWidht = 1;
  let planeHeight = 1;
  if(textureIcon.width > textureIcon.height){
    planeHeight = 1 * textureIcon.height / textureIcon.width;
  }else{
    planeWidht = 1 * textureIcon.width / textureIcon.height;
  }

  //icon
  const billboardIcon = new pc.Entity("BillboardIcon_"+amenityID);
  billboardIcon.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: textureIcon,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: new pc.Color(1,1,1),
    width:  5*baseSize * planeWidht,
    height: 5*baseSize * planeHeight,
    useInput: false,
  });
  billboardIcon.element!.layers = [pc.LAYERID_WORLD];
  billboardIcon.setLocalPosition(0, 0, baseSize);
  billboardIcon.setLocalScale(0.5, 0.5, 0.5);//0.707 (100%), ≅0.5(70%)
  billboardBackground.addChild(billboardIcon);

  billboardParent.script?.create('amenityBehavior', {
    attributes: {
      billboardID: amenityID,
      freeRotation: true,
      billboardParent: billboardParent,
      startPointEntity: startPointEntity,
      endPointEntity: endPointEntity,
      billboardEntity: billboardBackground,
    }
  });
  billboardParent.script?.["amenityBehavior"].ChangeLookAtEntity(cameraEntity);
  billboardParent.script?.["amenityBehavior"].SetPortraitRef(isPortraitRef);


  //CREACION DE TUBO
  const tubeMaterial = CreateLayoutMaterialFromColor("tubeMaterial@"+amenityID, new pc.Color(1, 1, 1), true, 1);
  const tube = new pc.Entity();
  tube.addComponent("render", {
    type: "cylinder",
    material: tubeMaterial
  });

  billboardParent.addChild(tube);
  const direction = endPointEntity.getPosition().clone().sub(billboardParent.getPosition().clone());
  const length = direction.length();

  const midpoint = direction.clone().mulScalar(0.5);
  tube.setLocalPosition(midpoint);
  tube.setLocalScale(0.25 * baseSize, length, 0.25 * baseSize);

  const up = new pc.Vec3(0, 1, 0);
  const quat = new pc.Quat();
  quat.setFromDirections(up, direction.clone().normalize());
  tube.setRotation(quat);
  EnablePickRecursive(tube, false);
  //tube.model!.layers = [pc.LAYERID_WORLD];

  return worldScreen;
}

export function CreateCapsuleBillboardTextNode(billboardAssets: any, billboardEnd: pc.Entity, detailID: string, fontAsset: number, texto: string, color: pc.Color, colorFill: pc.Color, scale: number, addDonut: boolean, useInput: boolean = true) : pc.Entity {
  const worldScreen = new pc.Entity("WorldScreen_"+texto);
  worldScreen.addComponent("screen", {
    screenSpace: false,
    resolution: pc.Vec2.ONE,
    referenceResolution: new pc.Vec2(1280, 720),
    //scaleMode: "blend",
  });
  worldScreen.setLocalEulerAngles(0, 180, 0);
  worldScreen.setLocalScale(0.01, 0.01, 0.01);

  let roundBG: pc.Entity|null = null;
  if(addDonut){
    roundBG = new pc.Entity("roundBG_"+texto);
    roundBG.addComponent("element", {
      type: pc.ELEMENTTYPE_IMAGE,
      texture: billboardAssets.hotspotTexture,
      pivot: new pc.Vec2(0.5, 0.5),
      anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
      color: new pc.Color(1,1,1),
      width: 1000,
      height: 1000,
      useInput: useInput,
    });
    roundBG.element!.layers = [pc.LAYERID_WORLD];
    roundBG.addComponent('script');
    roundBG.script?.create('pulseEffect', {
      attributes: {
        minScale: 0.5,
        maxScale: 0.9,
        period: 2,
      }
    });
    roundBG.setLocalPosition(0, 500, 0);
    worldScreen.addChild(roundBG);
  }

  const billboard = new pc.Entity("Billboard_"+texto);
  billboard.setLocalPosition(0, addDonut ? 1700 : 700, 0);
  billboard.addComponent("element",{width: 0, height: 0});
  billboard.element!.layers = [pc.LAYERID_WORLD];
  worldScreen.addChild(billboard);

  //center
  const centerBillboard = new pc.Entity("BG"+texto);
  centerBillboard.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.hierarquicalCenterTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: colorFill,
    width: 1000,
    height: 1000,
    useInput: useInput,
  });
  centerBillboard.element!.layers = [pc.LAYERID_WORLD];
  billboard.addChild(centerBillboard);

  //left
  const leftBillboard = new pc.Entity("BG"+texto);
  leftBillboard.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.hierarquicalLeftTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: colorFill,
    width: 1000 * 100 / 185,
    height: 1000,
    useInput: useInput,
  });
  leftBillboard.element!.layers = [pc.LAYERID_WORLD];
  billboard.addChild(leftBillboard);

  //right
  const rightBillboard = new pc.Entity("BG"+texto);
  rightBillboard.addComponent("element", {
    type: pc.ELEMENTTYPE_IMAGE,
    texture: billboardAssets.hierarquicalRightTexture,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    color: colorFill,
    width: 1000 * 100 / 185,
    height: 1000,
    useInput: useInput,
  });
  rightBillboard.element!.layers = [pc.LAYERID_WORLD];
  billboard.addChild(rightBillboard);

  //text
  const text = new pc.Entity("Text3D_"+texto);
  text.addComponent("button");
  text.addComponent("element", {
    type: pc.ELEMENTTYPE_TEXT,
    fontAsset: fontAsset,
    text: ` ${texto} `,
    pivot: new pc.Vec2(0.5, 0.5),
    anchor: new pc.Vec4(0.5, 0.5, 0.5, 0.5),
    Height: 1000,
    fontSize: 400,
    color: color,
    useInput: useInput,
  });
  text.element!.layers = [pc.LAYERID_WORLD];
  text.setLocalPosition(0,0,50);
  centerBillboard.addChild(text);

  billboardEnd.script?.create('billboardCapsuleScalerAndLookAt', {
    attributes: {
      billboardID: detailID,
      endEntity: billboardEnd,
      scaleReduction: scale,
      freeRotation: true,
      textEntity: text,
      lowerBillboardEntity: roundBG,
      upperBillboardEntity: billboard,
      leftBillboardEntity: leftBillboard,
      centerBillboardEntity: centerBillboard,
      rightBillboardEntity: rightBillboard,
    }
  });

  return worldScreen;
}

export function SetNodeMaterial(node: pc.GraphNode|pc.Entity, material: pc.Material) {
  const entityNode = node as pc.Entity;
  if(!entityNode.render)
    return;
  entityNode.render.material = material;
  for(const mesh of entityNode.render.meshInstances){
    mesh.material = material;
  }
}

export function CustomResize() {
  app?.resizeCanvas();
  let scaler = isMobile ? MobileScaler : DestopScaler;
  if(canvas.clientWidth > 2000 || canvas.clientHeight  > 2000)
    scaler = 1;
  app?.setCanvasResolution(pc.RESOLUTION_FIXED, canvas.clientWidth * scaler, canvas.clientHeight * scaler);
}

////////////////////////////////CREATION////////////////////////////

export async function CreateScene(engine: "spin"|"gsplat", modelsURLs: any, setProgress: (value: number) => void, browserInfo: any = null, paramQuality: string = "", paramProfiler: string = "", introAnimation: boolean = false, showCameraSpherical: boolean = false) {

  const options: any = {
    elementInput: new pc.ElementInput(canvas as any),
  }
  if(engine === "spin"){
    options.graphicsDeviceOptions = { alpha: true, premultipliedAlpha: false, antialias: true };
  }
  if(engine === "gsplat"){
    options.mouse = new pc.Mouse(canvas as any);
    options.touch = new pc.TouchDevice(canvas as any);
  }
  
  const app = new pc.Application(canvas, options);

  (app.loader.getHandler('texture') as any).crossOrigin = 'anonymous';
  (app.loader.getHandler('material') as any).crossOrigin = 'anonymous';
  (app.loader.getHandler('json') as any).crossOrigin = 'anonymous';
  
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  if(engine === "spin"){
    app.setCanvasFillMode(pc.FILLMODE_NONE);
    app.scene.skybox = null;
  }
  if(engine === "gsplat"){
    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  }
  SetApp(app);
  SetIsMobile(browserInfo.isDeviceMobile === true);

  let isHighPerformance = false;
  if(engine === "gsplat"){
    if(paramQuality === 'high'){
      isHighPerformance = true;
    }else if(paramQuality === 'low'){
      isHighPerformance = false;
    }else{
      const info = await GetPerformanceInfo(paramProfiler === 'true');
      isHighPerformance = info.isHighEnd;
    }
  }

  let gsplatLayer: pc.Layer|null = null;
  if(engine === "gsplat"){
    gsplatLayer = new pc.Layer({ name: "GSplatLayer", opaqueSortMode: pc.SORTMODE_MANUAL, transparentSortMode: pc.SORTMODE_MANUAL});
    app.scene.layers.insert(gsplatLayer, 0);
  }

  await LoadDracoFiles();
  //LOAD SCRIPTS:
  const scriptsAssets: any = {
    outlineHull: new pc.Asset('outlineHull', 'script', { url: "./pc/scripts/OutlineHull.js" }),

    amenityBehavior: new pc.Asset('amenityBehavior', 'script', { url: "./pc/scripts/AmenityBehavior.js" }),
    billboardScalerAndLookAt: new pc.Asset('billboardScalerAndLookAt', 'script', { url: "./pc/scripts/BillboardScalerAndLookAt.js" }),
    propertyBehavior: new pc.Asset('propertyBehavior', 'script', { url: "./pc/scripts/PropertyBehavior.js" }),
    
    //cameraAmenityAnimation: new pc.Asset('cameraAmenityAnimation', 'script', { url: "./pc/scripts/CameraAmenityAnimation.js" }),
    //cameraPropertyAnimation: new pc.Asset('cameraPropertyAnimation', 'script', { url: "./pc/scripts/CameraPropertyAnimation.js" }),
    pulseEffect: new pc.Asset('pulseEffect', 'script', { url: "./pc/scripts/PulseEffect.js" }),
    //lookAtobject: new pc.Asset('lookAtObject', 'script', { url: "./pc/scripts/LookAtObject.js" }),
  };
  if(engine === "gsplat"){
    scriptsAssets.orbitCamera = new pc.Asset('orbitCamera', 'script', { url: "./pc/scripts/OrbitCamera.js" });
    scriptsAssets.keyboardInput = new pc.Asset('keyboardInput', 'script', { url: "./pc/scripts/KeyboardInput.js" });
    scriptsAssets.mouseInput = new pc.Asset('mouseInput', 'script', { url: "./pc/scripts/MouseInput.js" });
    scriptsAssets.touchInput = new pc.Asset('touchInput', 'script', { url: "./pc/scripts/TouchInput.js" });

    scriptsAssets.splatFrag = new pc.Asset('splatFrag', 'text', { url: "./pc/shaders/splat.frag.glsl" });
    scriptsAssets.splatVert = new pc.Asset('splatVert', 'text', { url: "./pc/shaders/splat.vert.glsl" });
  }

  await LoadMultipleAssets(scriptsAssets, setProgress, 0, 6); // de 0% a 6%
  
  //LOAD TEXTURES
  const texturesAssets = {
    //textures
    baseBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/base_billboard.png`}),
    roundBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/round_billboard.png`}),
    hierarquicalBillboardLeft: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_left.png`}),
    hotspotBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hotspot_billboard.png`}),
    hierarquicalBillboardRight: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_right.png`}),
    hierarquicalBillboardCenter: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_center.png`}),
    montserratMediumFont: new pc.Asset('montserratMediumFont', 'font', { url: `./models/fonts/Montserrat-Medium.json`}),
  };
  await LoadMultipleAssets(texturesAssets, setProgress, 6, 30); // de 6% a 30%

  //LOAD MODELS
  const modelsAssets: any = {
    billboard: new pc.Asset('billboard', 'container', { url: `./models/billboard.glb`}),
    project: new pc.Asset('project', 'container', { url: modelsURLs.project}),//`./models/MasterPlan_Poseidonia.glb` || modelsURLs.project
  };
  if(engine === "gsplat"){
    modelsAssets.dome = new pc.Asset('dome', 'container', { url: browserInfo.isDeviceMobile === true ? `./models/dome_mobile_v3.glb` : `./models/dome_desktop_v3.glb`});
  }
  const start = performance.now();
  await LoadMultipleAssets(modelsAssets, setProgress, 30, 60); // de 30% a 60%
  const time = (performance.now() - start) / 1000; //segundos

  //LOAD GAUSSIAN
  let gaussianAsset : pc.Asset | null = null;
  if(engine === "gsplat"){
    const gaussianAsset = new pc.Asset('gsplat', 'gsplat', { url: isHighPerformance === true ? "./models/sogs_high_v1/meta.json" : "./models/sogs_low_v2/meta.json"});
    await LoadSingleAsset(gaussianAsset, setProgress, 60, 97); //de 60% a 97%
  }

  app.start();
  const showProfiler = paramProfiler === 'true';

  if(showProfiler === true){
    //PROFILER
    const opts: any = pc.MiniStats.getDefaultOptions();
    opts.cpu.watermark = 10; //Subimos la barra roja de “budget” de la CPU a 10 ms
    opts.gpu.enabled = true; //Desactivamos la gráfica de GPU (útil si tu HW no soporta queries)
    opts.sizes.unshift({ width: 100, height: 30, spacing: 2, graphs: true }); //Añadimos un tamaño "micro" (160×80 px) como primera opción
    opts.startSizeIndex = 0;
    const mini = new pc.MiniStats(app, opts);
    mini.div.classList.add('mini-stats-top');
  }

  // Create a camera
  const cameraEntity = new pc.Entity('Camera');
  const cameraComponent = cameraEntity.addComponent('camera');
  SetMainCamera(cameraComponent as pc.CameraComponent);

  if(engine === "spin"){
    cameraEntity.camera!.clearColor = new pc.Color(0, 0, 0, 0);
    cameraEntity.camera!.nearClip = 10;
    cameraEntity.camera!.farClip = 10000;
  }
  if(engine === "gsplat"){
    cameraEntity.camera!.clearColor = new pc.Color(0.0, 0.0, 0.0);
    cameraEntity.camera!.layers.push(gsplatLayer!.id);
    cameraEntity.addComponent('script');
    cameraEntity.script?.create('orbitCamera', {
      attributes: {
        distanceMax: 80,
        distanceMin: 36,
        distanceStart: 60,
        pitchAngleMax: 85, //opcional
        pitchAngleMin: 15, //opcional
        autoRotationIdleTime: 30, //opcional
        autoRotationValue: 5, //opcional
        printCameraCoords: showCameraSpherical, //opcional
        inertiaFactor: 0.2,
        frameOnStart: false
      }
    });
    cameraEntity.script?.create('touchInput', {
      attributes: {
        orbitSensitivity: 0.4,
        distanceSensitivity: 0.2,
        enablePan: false,
      }
    });
    cameraEntity.script?.create('mouseInput', {
      attributes: {
        orbitSensitivity: 0.3,
        distanceSensitivity: 0.18,
        enablePan: false,
      }
    });
    cameraEntity.script?.create('cameraAmenityAnimation');
    cameraEntity.script?.create('cameraPropertyAnimation');
  }

  app.root.addChild(cameraEntity);

  // Add a directional light
  const light = new pc.Entity('Light');
  light.addComponent('light', {
    type: 'directional',
  });
  light.setEulerAngles(45, 30, 0);
  app.root.addChild(light);

  let gaussianResults: any = {};
  if(engine === "gsplat"){
    gaussianResults = CreateSplatInstance('gsplat', gaussianAsset!, gsplatLayer, introAnimation, scriptsAssets.splatVert.resources[0], scriptsAssets.splatFrag.resources[0]);
    gaussianResults.gsplatNode.setLocalPosition(0, 0, 0);
    gaussianResults.gsplatNode.setLocalScale(1, 1, 1);
    EnablePickRecursive(gaussianResults.gsplatNode, false);
  }

  //LOGIC
  const billboardAssets: any = {};
  {
    //SETUP BILLBOARD TEXTURES
    app.assets.add(texturesAssets.baseBillboard);
    app.assets.add(texturesAssets.roundBillboard);
    app.assets.add(texturesAssets.hotspotBillboard);
    app.assets.add(texturesAssets.hierarquicalBillboardLeft);
    app.assets.add(texturesAssets.hierarquicalBillboardRight);
    app.assets.add(texturesAssets.hierarquicalBillboardCenter);
    
    billboardAssets.amenityBaseTexture = SoftTextures(texturesAssets.baseBillboard.resources[0] as pc.Texture);
    billboardAssets.propertyTexture = SoftTextures(texturesAssets.roundBillboard.resources[0] as pc.Texture);
    billboardAssets.hotspotTexture = SoftTextures(texturesAssets.hotspotBillboard.resources[0] as pc.Texture);
    billboardAssets.hierarquicalLeftTexture = SoftTextures(texturesAssets.hierarquicalBillboardLeft.resources[0] as pc.Texture);
    billboardAssets.hierarquicalRightTexture = SoftTextures(texturesAssets.hierarquicalBillboardRight.resources[0] as pc.Texture);
    billboardAssets.hierarquicalCenterTexture = SoftTextures(texturesAssets.hierarquicalBillboardCenter.resources[0] as pc.Texture);
  }
  {
    //LOAD BILLBOARD
    const billboardParent = new pc.Entity("billboardParent");
    billboardParent.enabled = false;
    app.root.addChild(billboardParent);

    const billboard = (modelsAssets.billboard.resources[0] as any).instantiateRenderEntity() as pc.Entity;
    billboardParent.addChild(billboard);
    billboard.setLocalEulerAngles(new pc.Vec3(-90, 0, 0) );
    
    const materialEdge = CreateLayoutMaterialFromColor("billboardEdge", new pc.Color(1.0,1.0,1.0), false, 1.0);
    const materialFill = CreateLayoutMaterialFromColor("billboardFill", new pc.Color(0.9,0.9,0.9), true, 0.9);

    for (const billboardMesh of billboard.children){
      const material = billboardMesh.name.toLocaleLowerCase().includes("edge") ? materialEdge : materialFill;
      SetNodeMaterial(billboardMesh, material);
    }
    EnablePickRecursive(billboardParent, false);
    billboardAssets.ammenityMesh = billboardParent;
  }

  if(engine === "gsplat"){//Add dome
    const dome = (modelsAssets.dome.resources[0] as any).instantiateRenderEntity() as pc.Entity;
    app.root.addChild(dome);
    AssignLayerRecursive(dome, gsplatLayer!.id);
  }
  

  const rootEntity = (modelsAssets.project.resources[0] as any).instantiateRenderEntity() as pc.Entity;
  app.root.addChild(rootEntity);
  
  //busca al nodo masterplan, desactiva el pickind en los demás (si existen)
  let masterplanEntity: pc.Entity | null = null;
  if(rootEntity.name.toLocaleLowerCase().includes("masterplan")){
    masterplanEntity = rootEntity;
  }else{
    for(const masterplanNode of rootEntity.children){
      if(masterplanNode.name.toLocaleLowerCase().includes("masterplan")){
        masterplanEntity = masterplanNode as pc.Entity;
      }else{
        EnablePickRecursive(masterplanNode, false);
      }
    }
  }

  //extracción de las animaciones
  let animComponent: pc.AnimComponent | null = null
  let animFps = 0;
  if(engine === "spin" && masterplanEntity){

    const projectID = GetIDFromNodeName(masterplanEntity.name);
    animComponent = rootEntity.addComponent("anim", {
      activate: true,// arranca el sistema de anim en cuanto tenga un clip
    }) as pc.AnimComponent;

    const animations = (modelsAssets.project.resources[0] as any).animations as pc.Asset[];
    const graph = {
      layers: [...animations.map((animation) => (
          {
            name: animation.resources[0]["name"],
            states: [
              {
                name: 'START'
              },{
                name: 'ANIMATION',
                speed: 0,
                defaultState: true,
              },
              {
                name: 'END'
              }
            ],
            transitions: [
              {
                from: 'START',
                to: 'ANIMATION',
              }
            ]
          }
          )),
      ],
      parameters: {}
    };
    animComponent.loadStateGraph(graph);
    for(const animation of animations){
      const animTrack = animation.resources[0] as pc.AnimTrack;
      const animationName = animTrack.name;
      const layer = animComponent.findAnimationLayer(animationName);
      layer.assignAnimation("ANIMATION", animTrack);
      if(animationName.toLowerCase().includes(projectID.toLowerCase())){
        const totalFrames = animTrack.outputs[0].data.length / animTrack.outputs[0].components;
        const totalDuration = animTrack.duration;
        animFps = totalFrames / totalDuration;  //actualmente es aprox 24
      }
    }
  }
  

  const results = {
    app: app,
    cameraEntity: cameraEntity,
    showProfiler: showProfiler,
    rootEntity: rootEntity,
    billboardAssets: billboardAssets,
    gsplatNode: gaussianResults.gsplatNode,
    gsplat: gaussianResults.gsplat,
    gsplatMaterial: gaussianResults.gsplatMaterial,
    montserratMediumFontID: texturesAssets.montserratMediumFont.id,

    masterplanEntity: masterplanEntity,
    animComponent: animComponent,
    animFps: animFps,
  }

  return results;
}

//////////Creation3D

export async function CreateScene3D(modelsURLs: any, setProgress: (value: number) => void, browserInfo: any, paramQuality: string, paramProfiler: string, introAnimation: boolean, showCameraSpherical: boolean) {

  const device = await pc.createGraphicsDevice(canvas, {
    //deviceTypes: [pc.DEVICETYPE_WEBGPU, pc.DEVICETYPE_WEBGL2], // prefer WebGPU
    powerPreference: 'high-performance',
    antialias: false, // splats usually prefer MSAA off
    // Only needed if you use custom GLSL chunks/shaders and want them auto-transpiled to WGSL:
    // glslangUrl: '/path/to/glslang.js',
    // twgslUrl:   '/path/to/twgsl.js'
  });

  const app = new pc.Application(canvas, {
    graphicsDevice: device,
    mouse: new pc.Mouse(canvas as any),
    touch: new pc.TouchDevice(canvas as any),
    elementInput: new pc.ElementInput(canvas as any),
  });

  (app.loader.getHandler('texture') as any).crossOrigin = 'anonymous';
  (app.loader.getHandler('material') as any).crossOrigin = 'anonymous';
  (app.loader.getHandler('json') as any).crossOrigin = 'anonymous';

  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  if (browserInfo.isDeviceMobile === true) app.graphicsDevice.maxPixelRatio = 1;
  SetApp(app);
  SetIsMobile(browserInfo.isDeviceMobile === true);

  let isHighPerformance = false;
  if(paramQuality === 'high'){
    isHighPerformance = true;
  }else if(paramQuality === 'low'){
    isHighPerformance = false;
  }else{
    const info = await GetPerformanceInfo(paramProfiler === 'true');
    isHighPerformance = info.isHighEnd;
  }

  //gsplat layer
  const gsplatLayer = new pc.Layer({ name: "GSplatLayer", opaqueSortMode: pc.SORTMODE_MANUAL, transparentSortMode: pc.SORTMODE_MANUAL, });
  app.scene.layers.insert(gsplatLayer, 0);

  await LoadDracoFiles();
  //LOAD SCRIPTS:
  const scriptsAssets = {
    orbitCamera: new pc.Asset('orbitCamera', 'script', { url: "./pc/scripts/gsplat/OrbitCamera.js" }),
    pulseEffect: new pc.Asset('pulseEffect', 'script', { url: "./pc/scripts/gsplat/PulseEffect.js" }),

    cameraAmenityAnimation: new pc.Asset('cameraAmenityAnimation', 'script', { url: "./pc/scripts/gsplat/CameraAmenityAnimation.js" }),
    cameraPropertyAnimation: new pc.Asset('cameraPropertyAnimation', 'script', { url: "./pc/scripts/gsplat/CameraPropertyAnimation.js" }),
    cameraTownplanningAnimation: new pc.Asset('cameraTownplanningAnimation', 'script', { url: "./pc/scripts/gsplat/CameraTownplanningAnimation.js" }),
    propertyBehavior: new pc.Asset('propertyBehavior', 'script', { url: "./pc/scripts/gsplat/PropertyBehavior.js" }),
    billboardScalerAndLookAt: new pc.Asset('billboardScalerAndLookAt', 'script', { url: "./pc/scripts/gsplat/BillboardScalerAndLookAt.js" }),
    billboardCapsuleScalerAndLookAt: new pc.Asset('billboardCapsuleScalerAndLookAt', 'script', { url: "./pc/scripts/gsplat/BillboardCapsuleScalerAndLookAt.js" }),
    

    keyboardInput: new pc.Asset('keyboardInput', 'script', { url: "./pc/scripts/gsplat/KeyboardInput.js" }),
    mouseInput: new pc.Asset('mouseInput', 'script', { url: "./pc/scripts/gsplat/MouseInput.js" }),
    touchInput: new pc.Asset('touchInput', 'script', { url: "./pc/scripts/gsplat/TouchInput.js" }),
    lookAtobject: new pc.Asset('lookAtObject', 'script', { url: "./pc/scripts/gsplat/LookAtObject.js" }),
    amenityBehavior: new pc.Asset('amenityBehavior', 'script', { url: "./pc/scripts/gsplat/AmenityBehavior.js" }),
    splatFrag: new pc.Asset('splatFrag', 'text', { url: "./pc/shaders/gsplat/splat.frag.glsl" }),
    splatVert: new pc.Asset('splatVert', 'text', { url: "./pc/shaders/gsplat/splat.vert.glsl" }),
  };

  await LoadMultipleAssets(scriptsAssets, setProgress, 0, 6); // de 0% a 6%
  
  //LOAD TEXTURES
  const texturesAssets = {
    //textures
    baseBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/base_billboard.png`}),
    roundBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/round_billboard.png`}),
    hierarquicalBillboardLeft: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_left.png`}),
    hotspotBillboard: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hotspot_billboard.png`}),
    hierarquicalBillboardRight: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_right.png`}),
    hierarquicalBillboardCenter: new pc.Asset('roundBillboardTexture', 'texture', { url: `./models/textures/hierarquical_billboard_center.png`}),
    montserratMediumFont: new pc.Asset('montserratMediumFont', 'font', { url: `./models/fonts/Montserrat-Medium.json`}),
  };
  await LoadMultipleAssets(texturesAssets, setProgress, 6, 30); // de 6% a 30%

  //LOAD MODELS
  const modelsAssets = {
    //models
    project: new pc.Asset('project', 'container', { url: modelsURLs.project}),
    billboard: new pc.Asset('billboard', 'container', { url: `./models/billboard.glb`}),
    dome: new pc.Asset('dome', 'container', { url: browserInfo.isDeviceMobile === true ? modelsURLs.dome_mobile : modelsURLs.dome_desktop}),
  };
  const start = performance.now();
  await LoadMultipleAssets(modelsAssets, setProgress, 30, 60); // de 30% a 60%
  const time = (performance.now() - start) / 1000; //segundos

  //LOAD GAUSSIAN
  const gaussianAsset = new pc.Asset('gsplat', 'gsplat', { url: isHighPerformance === true ? modelsURLs.sogs_high : modelsURLs.sogs_low}, { crossOrigin: 'anonymous' });
  await LoadSingleAsset(gaussianAsset, setProgress, 60, 97); //de 60% a 97%
  

  app.start();
  const showProfiler = paramProfiler === 'true';

  if(showProfiler === true){
    //PROFILER
    const opts: any = pc.MiniStats.getDefaultOptions();
    opts.cpu.watermark = 10; //Subimos la barra roja de “budget” de la CPU a 10 ms
    opts.gpu.enabled = true; //Desactivamos la gráfica de GPU (útil si tu HW no soporta queries)
    opts.sizes.unshift({ width: 100, height: 30, spacing: 2, graphs: true }); //Añadimos un tamaño "micro" (160×80 px) como primera opción
    opts.startSizeIndex = 0;
    const mini = new pc.MiniStats(app, opts);
    mini.div.classList.add('mini-stats-top');
  }

  // Create a camera
  const cameraEntity = new pc.Entity('Camera');
  cameraEntity.addComponent('camera', { clearColor: new pc.Color(0.0, 0.0, 0.0) });
  cameraEntity.camera!.layers.push(gsplatLayer.id);
  // cameraEntity.camera!.nearClip = 0.1;
  // cameraEntity.camera!.farClip = 1000;
  cameraEntity.addComponent('script');

  cameraEntity.script?.create('orbitCamera', {
    attributes: {
      distanceMax: 1000,
      distanceMin: 1,
      distanceStart: 10,
      pitchAngleMax: 70, //opcional
      pitchAngleMin: 10, //opcional
      autoRotationIdleTime: 30, //opcional
      autoRotationValue: 5, //opcional
      printCameraCoords: showCameraSpherical, //opcional
      inertiaFactor: 0.2,
      frameOnStart: false
    }
  });
  cameraEntity.script?.create('touchInput', {
    attributes: {
      orbitSensitivity: 0.4,
      distanceSensitivity: 0.2,
      enablePan: false,
    }
  });
  cameraEntity.script?.create('mouseInput', {
    attributes: {
      orbitSensitivity: 0.3,
      distanceSensitivity: 0.18,
      enablePan: false,
    }
  });
  cameraEntity.script?.create('cameraAmenityAnimation');
  cameraEntity.script?.create('cameraPropertyAnimation');
  cameraEntity.script?.create('cameraTownplanningAnimation');

  app.root.addChild(cameraEntity);

  // Add a directional light
  const light = new pc.Entity('Light');
  light.addComponent('light', {
    type: 'directional',
  });
  light.setEulerAngles(45, 30, 0);
  app.root.addChild(light);

  const gaussianResults = CreateSplatInstance('gsplat', gaussianAsset, gsplatLayer, introAnimation, scriptsAssets.splatVert.resources[0], scriptsAssets.splatFrag.resources[0]);
  gaussianResults.gsplatNode.setLocalPosition(0, 0, 0);
  gaussianResults.gsplatNode.setLocalScale(1, 1, 1);
  EnablePickRecursive(gaussianResults.gsplatNode, false);

  //LOGIC
  const billboardAssets: any = {};
  {
    //SETUP BILLBOARD TEXTURES
    app.assets.add(texturesAssets.baseBillboard);
    app.assets.add(texturesAssets.roundBillboard);
    app.assets.add(texturesAssets.hotspotBillboard);
    app.assets.add(texturesAssets.hierarquicalBillboardLeft);
    app.assets.add(texturesAssets.hierarquicalBillboardRight);
    app.assets.add(texturesAssets.hierarquicalBillboardCenter);

    billboardAssets.amenityBaseTexture = SoftTextures(texturesAssets.baseBillboard.resources[0] as pc.Texture);
    billboardAssets.propertyTexture = SoftTextures(texturesAssets.roundBillboard.resources[0] as pc.Texture);
    billboardAssets.hotspotTexture = SoftTextures(texturesAssets.hotspotBillboard.resources[0] as pc.Texture);
    billboardAssets.hierarquicalLeftTexture = SoftTextures(texturesAssets.hierarquicalBillboardLeft.resources[0] as pc.Texture);
    billboardAssets.hierarquicalRightTexture = SoftTextures(texturesAssets.hierarquicalBillboardRight.resources[0] as pc.Texture);
    billboardAssets.hierarquicalCenterTexture = SoftTextures(texturesAssets.hierarquicalBillboardCenter.resources[0] as pc.Texture);
  }
  {
    //LOAD BILLBOARD
    const billboardParent = new pc.Entity("billboardParent");
    billboardParent.enabled = false;
    app.root.addChild(billboardParent);

    const billboard = (modelsAssets.billboard.resources[0] as any).instantiateRenderEntity() as pc.Entity;
    billboardParent.addChild(billboard);
    billboard.setLocalEulerAngles(new pc.Vec3(-90, 0, 0) );
    
    const materialEdge = CreateLayoutMaterialFromColor("billboardEdge", new pc.Color(1.0,1.0,1.0), false, 1.0);
    const materialFill = CreateLayoutMaterialFromColor("billboardFill", new pc.Color(0.9,0.9,0.9), true, 0.9);

    for (const billboardMesh of billboard.children){
      const material = billboardMesh.name.toLocaleLowerCase().includes("edge") ? materialEdge : materialFill;
      SetNodeMaterial(billboardMesh, material);
    }
    EnablePickRecursive(billboardParent, false);
    billboardAssets.ammenityMesh = billboardParent;
  }

  //AssignLayerRecursive
  const dome = (modelsAssets.dome.resources[0] as any).instantiateRenderEntity() as pc.Entity;
  app.root.addChild(dome);
  AssignLayerRecursive(dome, gsplatLayer.id);

  const rootEntity = (modelsAssets.project.resources[0] as any).instantiateRenderEntity() as pc.Entity;
  app.root.addChild(rootEntity);

  const results = {
    app: app,
    cameraEntity: cameraEntity,
    showProfiler: showProfiler,
    rootEntity: rootEntity,
    billboardAssets: billboardAssets,
    gsplatNode: gaussianResults.gsplatNode,
    gsplat: gaussianResults.gsplat,
    gsplatMaterial: gaussianResults.gsplatMaterial,
    montserratMediumFontID: texturesAssets.montserratMediumFont.id,
  }

  return results;
}